import React, { useRef } from 'react';
import { Button } from 'antd';
import { FileDoneOutlined } from '@ant-design/icons';
import useGeneratePDF from '../../hooks/useGeneratePDF';
import '../../styles/reports.scss';

interface Route {
  trip_id: number;
  vehicle_id: string;
  distance_actual: number;
  distance_optimized: number;
  distance_unoptimized: number;
  vehicle_capacity: number;
  locations_capacity: number;
}

interface MileageReportProps {
  routes: Route[];
  totalDistance: number;
  optimizedDistance: number;
  totalActualDistance: number;
  // eslint-disable-next-line react/require-default-props
  dateRange?: { start?: string; end?: string };
}

interface TemplateProps extends MileageReportProps {
  templateRef: React.RefObject<HTMLDivElement>;
}

const MileageReportTemplate = ({
  templateRef,
  routes,
  totalDistance,
  optimizedDistance,
  totalActualDistance,
  dateRange,
}: TemplateProps) => {
  const dateLabel = dateRange?.start && dateRange?.end ? `${dateRange.start} to ${dateRange.end}` : 'All Dates';

  const routeAdherence = (() => {
    if (!totalActualDistance || !optimizedDistance || optimizedDistance === 0) return 'N/A';
    const val = Math.min(1, 2 - totalActualDistance / optimizedDistance) * 100;
    return val < 0 ? '0.00%' : `${val.toFixed(2)}%`;
  })();

  const summaryItems: [string, string][] = [
    ['Date Range', dateLabel],
    ['Total Routes', String(routes.length)],
    ['Total Distance (Estimated)', `${(totalDistance || 0).toFixed(2)} Km`],
    ['Total Actual Distance', `${(totalActualDistance || 0).toFixed(2)} Km`],
    ['Total Optimized Distance', `${(optimizedDistance || 0).toFixed(2)} Km`],
    ['Route Adherence', routeAdherence],
  ];

  const headers = [
    'Route ID',
    'Vehicle',
    'Actual Distance (Km)',
    'Optimized Distance (Km)',
    'Estimated Distance (Km)',
    'Adherence',
  ];

  return (
    <div
      ref={templateRef}
      className="report report--w1100"
      style={{ '--report-accent': '#1a6eb5' } as React.CSSProperties}
    >
      <div className="report__header">
        <span className="report__title">Mileage Report — Route Wise</span>
        <span className="report__generated">Generated: {new Date().toLocaleString()}</span>
      </div>

      <div className="report__section-title">Summary</div>
      <div className="report__grid report__grid--mb24">
        {summaryItems.map(([label, value]) => (
          <div key={label} className="report__row">
            <div className="report__cell-label" style={{ '--section-bg': '#dbeafe' } as React.CSSProperties}>
              {label}
            </div>
            <div className="report__cell-value">{value}</div>
          </div>
        ))}
      </div>

      <div className="report__section-title">Route Mileage Details</div>
      <table className="report__table">
        <thead style={{ display: 'table-header-group' }}>
          <tr className="report__thead-row">
            {headers.map((h) => (
              <th key={h} className="report__th report__th--pad7">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {routes.map((r, i) => {
            const adherence = (() => {
              if (!r.distance_actual || !r.distance_optimized || r.distance_optimized === 0) return 'N/A';
              const actualKm = r.distance_actual / 1000;
              const optimizedKm = r.distance_optimized / 1000;
              const val = Math.min(1, 2 - actualKm / optimizedKm) * 100;
              return val < 0 ? '0.00%' : `${val.toFixed(2)}%`;
            })();
            return (
              <tr key={r.trip_id} className={i % 2 === 0 ? 'report__tr--even' : 'report__tr--odd'}>
                <td className="report__td report__td--pad6 report__td--bold">{r.trip_id}</td>
                <td className="report__td report__td--pad6">{r.vehicle_id}</td>
                <td
                  className="report__td report__td--pad6 report__td--accent"
                  style={{ '--cell-color': '#2980b9' } as React.CSSProperties}
                >
                  {((r.distance_actual || 0) / 1000).toFixed(2)}
                </td>
                <td className="report__td report__td--pad6"> {((r.distance_optimized || 0) / 1000).toFixed(2)}</td>
                <td className="report__td report__td--pad6">{((r.distance_unoptimized || 0) / 1000).toFixed(2)}</td>
                <td
                  className="report__td report__td--pad6 report__td--accent"
                  style={{ '--cell-color': '#8e44ad' } as React.CSSProperties}
                >
                  {adherence}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="report__footer">
        <span>RouteSonar — Mileage Report</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

MileageReportTemplate.displayName = 'MileageReportTemplate';

const MileageReport = ({ dateRange, ...rest }: MileageReportProps): JSX.Element => {
  const templateRef = useRef<HTMLDivElement>(null);
  const { generate, loading } = useGeneratePDF(templateRef, {
    fileName: `mileage-report.pdf`,
    orientation: 'landscape',
    avoidRowCuts: true,
  });

  return (
    <>
      <MileageReportTemplate templateRef={templateRef} dateRange={dateRange} {...rest} />
      <Button type="default" icon={<FileDoneOutlined />} loading={loading} onClick={generate}>
        {loading ? 'Generating...' : 'Mileage Report'}
      </Button>
    </>
  );
};

MileageReport.defaultProps = {
  dateRange: undefined,
};

export default MileageReport;
